import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Icon, Switch, theme } from '@sg/garnish';

import { usePaymentBreakdown } from '../../../../hooks';
import { BagHsaFsaInfoIcon } from '../../BagHsaFsaInfoIcon';

export const PaymentMethodSwitchers = (props: PaymentMethodSwitchersProps) => {
  const {
    isDisabled,
    paymentBreakdown,

    flexPaymentMethod,
    shouldUseFlex,
    amountEligibleForFlex,
    onChangeFlexUsage,

    shouldUseCredit,
    availableCredit,
    appliedCredit,
    onChangeCreditUsage,
  } = props;

  const { flexAmount, formattedTotalFlex, creditAmount, formattedTotalCredit } = paymentBreakdown;
  const { applied, available } = useCreditUsageHint(appliedCredit, availableCredit);

  const { formatMessage } = useIntl();
  const flexAriaLabelEnabled = formatMessage(messages.changeHsaFsaUsage, { amount: formattedTotalFlex }); // prettier-ignore
  const flexAriaLabelDisabled = formatMessage(messages.disabledChangeHsaFsaUsage); // prettier-ignore
  const flexAriaLabel = amountEligibleForFlex ? flexAriaLabelEnabled : flexAriaLabelDisabled;
  const creditAriaLabel = formatMessage(messages.changeCreditUsage, { amount: formattedTotalCredit }); // prettier-ignore

  return (
    <View style={styles.container}>
      <View style={styles.leftColumn}>
        {flexPaymentMethod ? (
          <View style={[styles.row, styles.leftRow]}>
            <View style={styles.info}>
              <Icon
                name="IconFlexCard"
                color={amountEligibleForFlex ? theme.colors.SPINACH : theme.colors.GRAY}
                width={32}
                height={32}
              />

              <BodyText
                style={amountEligibleForFlex ? styles.spinachText : styles.grayText}
                sizeMatch={['14']}
              >
                {flexPaymentMethod}
              </BodyText>

              {amountEligibleForFlex ? null : <BagHsaFsaInfoIcon />}
            </View>

            <Switch
              size="small"
              disabled={isDisabled || !amountEligibleForFlex}
              accessibilityLabel={flexAriaLabel}
              value={shouldUseFlex}
              onChange={onChangeFlexUsage}
            />
          </View>
        ) : null}

        {availableCredit > 0 ? (
          <View style={[styles.row, styles.leftRow]}>
            <View style={styles.info}>
              <Icon name="IconCredit" color={theme.colors.SPINACH} width={32} height={32} />

              <View>
                <BodyText style={styles.spinachText} sizeMatch={['14']}>
                  <FormattedMessage {...messages.creditUsageTitle} />
                </BodyText>

                <BodyText sizeMatch={['12']}>
                  <FormattedMessage {...messages.creditUsageHint} values={{ applied, available }} />
                </BodyText>
              </View>
            </View>

            <Switch
              size="small"
              disabled={isDisabled}
              accessibilityLabel={creditAriaLabel}
              value={shouldUseCredit}
              onChange={onChangeCreditUsage}
            />
          </View>
        ) : null}
      </View>

      <View aria-hidden={true}>
        {flexPaymentMethod ? (
          <View style={[styles.row, styles.priceWrapper]}>
            {amountEligibleForFlex ? (
              <BodyText
                style={[styles.priceText, getHighlightStyle(flexAmount)]}
                sizeMatch={['14']}
              >
                {formattedTotalFlex}
              </BodyText>
            ) : null}
          </View>
        ) : null}

        {availableCredit > 0 ? (
          <View style={[styles.row, styles.priceWrapper]}>
            <BodyText
              style={[styles.priceText, getHighlightStyle(creditAmount)]}
              sizeMatch={['14']}
            >
              {formattedTotalCredit}
            </BodyText>
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useCreditUsageHint = (appliedCredit: number, availableCredit: number) => {
  const { formatNumber } = useIntl();

  const available = formatNumber(Number(availableCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  const applied = formatNumber(Number(appliedCredit) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  return { applied, available };
};

function getHighlightStyle(amount: number) {
  if (amount > 0) {
    return styles.spinachText;
  }
}

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  disabledChangeHsaFsaUsage: {
    defaultMessage: 'HSA/FSA usage is not eligible for this purchase',
    description: 'Bag | Payment Methods | Disabled change HSA/FSA usage',
  },
  changeHsaFsaUsage: {
    defaultMessage: 'Change HSA/FSA usage for {amount}',
    description: 'Bag | Payment Methods | Change HSA/FSA usage',
  },
  changeCreditUsage: {
    defaultMessage: 'Change credit usage for {amount}',
    description: 'Bag | Payment Methods | Change credit usage',
  },
  creditUsageTitle: {
    defaultMessage: 'SG Credit',
    description: 'Bag | Payment Methods | Credit usage title',
  },
  creditUsageHint: {
    defaultMessage: 'Use {applied} of {available}',
    description: 'Bag | Payment Methods | Credit usage hint',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 56,
    gap: theme.spacing['2'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
  leftRow: {
    paddingLeft: theme.spacing['4'],
  },
  leftColumn: {
    flex: 1,
    width: '100%',
  },
  info: {
    flex: 1,
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  priceWrapper: {
    paddingLeft: theme.spacing['2'],
    paddingRight: theme.spacing['4'],
    justifyContent: 'flex-end',
  },
  priceText: {
    textAlign: 'right',
  },
  spinachText: {
    color: theme.colors.SPINACH,
  },
  grayText: {
    color: theme.colors.GRAY,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type PaymentMethodSwitchersProps = {
  isDisabled: boolean;
  paymentBreakdown: ReturnType<typeof usePaymentBreakdown>;

  // Flex switcher.

  flexPaymentMethod: string | null | undefined;
  shouldUseFlex: boolean;
  amountEligibleForFlex: number | null;
  onChangeFlexUsage: (shouldUseFlex: boolean) => void;

  // Credit switcher.

  shouldUseCredit: boolean;
  availableCredit: number;
  appliedCredit: number;
  onChangeCreditUsage: (shouldUseCredit: boolean) => void;
};
